import {
  Component,
  ElementRef,
  ViewChild,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnDestroy,
  ChangeDetectionStrategy,
  ChangeDetectorRef
} from '@angular/core';
import { StatoSessioneService } from 'app/shared/services/stato-sessione.service';
import { ApiService } from '../../../../shared/services/api.service';
import { SpinnerService } from '../../../../shared/services/spinner.service';
import { ToastrService } from 'ngx-toastr';
import { DownloaderComponent } from '../downloader/downloader.component';
import Swal from 'sweetalert2';
import {takeUntil, distinctUntilChanged, filter, take, finalize} from 'rxjs/operators';
import { Subject } from 'rxjs';
import { Router } from '@angular/router';
import { StreamProgressEvent } from '../../../../shared/interfaces/fileUploadInterfaces';

@Component({
  selector: 'app-test-file-upload',
  templateUrl: './test-file-upload.component.html',
  styleUrls: ['./test-file-upload.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TestFileUploadComponent implements OnInit, OnDestroy {
  @ViewChild('workspaceInput') workspaceInput: ElementRef<HTMLInputElement>;
  @ViewChild('fileInputDocx') fileInputDocx: ElementRef<HTMLInputElement>;

  @Input() templates: any[] = [];
  @Input() activeUploadMethod: 'story' | 'workspace' | null = null;
  @Input() projectId = ''; // Added to receive project ID from parent

  @Output() activeUploadMethodChange = new EventEmitter<'story' | 'workspace' | null>();
  @Output() templateSelected = new EventEmitter<string>();

  private destroy$ = new Subject<void>();
  readonly allowedStoryExtensions = ['txt', 'docx', 'pdf', 'md', 'html'];
  fileName = '';
  isUploadingStory = false;
  uploadProgressMessage = '';
  isDragOver = false; // drag n drop logic
  dragCounter = 0;

  readonly fileName$ = this.sessioneService.fileName$.pipe(
    filter(filename => filename !== undefined),
    distinctUntilChanged(),
    takeUntil(this.destroy$)
  );

  showMismatchContent = false;
  mismatchColumns: string[] = [];

  constructor(
    private sessioneService: StatoSessioneService,
    private spinnerService: SpinnerService,
    private toastr: ToastrService,
    private downloaderComponent: DownloaderComponent,
    private cdr: ChangeDetectorRef,
    private apiService: ApiService, // Added for uploading story files
    private router: Router // Added for navigation
  ) {}

  ngOnInit(): void {}

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // Story upload methods
  onStorySelected(): void {
    const input = this.fileInputDocx.nativeElement;
    if (input.files && input.files.length > 0) {
      const file = input.files[0];
      const ext = file.name.split('.').pop()?.toLowerCase();
      if (!ext || !this.allowedStoryExtensions.includes(ext)) {
        this.toastr.warning(`Only ${this.allowedStoryExtensions.join(', ')} files are supported.`, 'Invalid file type');
        input.value = '';
        return;
      }
      this.uploadFileToActivity(file);
      this.activeUploadMethodChange.emit('story');
    } else {
      this.fileName = '';
      this.isUploadingStory = false; // Ensure flag is false if no file selected
      this.cdr.markForCheck();
    }
  }

  // Helper to trigger the hidden input from the UI link
  triggerWorkspaceUpload(): void {
    this.workspaceInput.nativeElement.click();
  }

  uploadFileToActivity(file: File): void {
    const isPdf = file.name.toLowerCase().endsWith('.pdf');
    const isDocx = file.name.toLowerCase().endsWith('.docx');

    if (isPdf || isDocx) {
      this._uploadStoryWithStreaming(file, isPdf);
    } else {
      this._uploadStoryStandard(file);
    }
  }

  /** Standard upload (non-vision) */
  private _uploadStoryStandard(file: File): void {
    this.isUploadingStory = true;
    this.fileName = '';
    this.cdr.markForCheck();
    this.spinnerService.show();

    this.apiService.uploadFile(file, false)
      .pipe(
        take(1),
        takeUntil(this.destroy$),
        finalize(() => {
          this.isUploadingStory = false;
          this.spinnerService.hide();
          this.cdr.markForCheck();
        })
      )
      .subscribe({
        next: (response: any) => {
          this._handleUploadSuccess(file, response);
        },
        error: (error: any) => {
          this._handleUploadError(file, error);
        }
      });
  }

  /** Streaming upload with progress (for PDF vision and DOCX with images) */
  private _uploadStoryWithStreaming(file: File, useVision: boolean): void {
    this.isUploadingStory = true;
    this.uploadProgressMessage = 'Processing file...';
    this.fileName = '';
    this.cdr.markForCheck();

    let finalData: any = null;

    this.apiService.uploadFileStream(file, useVision).pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: (event: StreamProgressEvent) => {
        if (event.type === 'progress' && event.phase === 'vision') {
          this.uploadProgressMessage = `Analyzing page ${event.current}/${event.total}...`;
          this.cdr.markForCheck();
        } else if (event.type === 'progress' && event.phase === 'docx_images') {
          this.uploadProgressMessage = `Analyzing image ${event.current}/${event.total}...`;
          this.cdr.markForCheck();
        } else if (event.type === 'progress' && event.phase === 'saving') {
          this.uploadProgressMessage = 'Saving...';
          this.cdr.markForCheck();
        } else if (event.type === 'complete') {
          finalData = event.data;
        } else if (event.type === 'error') {
          console.error('Streaming upload error:', event.detail);
        }
      },
      error: (error: any) => {
        this._handleUploadError(file, error);
        this.isUploadingStory = false;
        this.uploadProgressMessage = '';
        this.cdr.markForCheck();
      },
      complete: () => {
        if (finalData) {
          this._handleUploadSuccess(file, finalData);
        }
        this.isUploadingStory = false;
        this.uploadProgressMessage = '';
        this.cdr.markForCheck();
      }
    });
  }

  /** Shared success handler */
  private _handleUploadSuccess(file: File, response: any): void {
    this.fileName = file.name;
    this.sessioneService.updateFileName(file.name);

    if (Array.isArray(response.contents)) {
      this.sessioneService.updateRefinedStoryString(response.contents.join('\n'));
    } else if (typeof response.contents === 'string') {
      this.sessioneService.updateRefinedStoryString(response.contents);
    } else {
      this.toastr.error('Unexpected response format for story content.');
    }

    this.activeUploadMethodChange.emit('story');
    this.toastr.success(`Story file "${file.name}" uploaded successfully.`);

    this.router.navigate(['/projects/story', this.projectId], {
      queryParams: { sessionLoaded: 'true', source: 'file' }
    });
  }

  /** Shared error handler */
  private _handleUploadError(file: File, error: any): void {
    console.error('There was an error uploading the file!', error);
    this.toastr.error(`Failed to upload story file "${file.name}".`);
    this.fileName = '';
    this.sessioneService.updateFileName(undefined);
    this.activeUploadMethodChange.emit(null);
  }

  // ---------------------------------------------------------
  // DRAG AND DROP HANDLERS
  // ---------------------------------------------------------

  onDragEnter(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();

    if (this.isUploadingStory) return;

    this.dragCounter++; // Increment counter
    this.isDragOver = true;
  }

  // 2. Update DragOver
  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    // Logic handles in DragEnter, but we need this to prevent browser default opening file
    if (this.isUploadingStory) return;
    this.isDragOver = true;
  }

  // 3. Update DragLeave (Use Counter)
  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();

    this.dragCounter--; // Decrement counter

    // Only disable drag state if counter is 0 (cursor actually left the container)
    if (this.dragCounter === 0) {
      this.isDragOver = false;
    }
  }

  // 4. Update Drop (Reset Counter)
  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();

    // Reset everything
    this.isDragOver = false;
    this.dragCounter = 0;

    if (this.isUploadingStory) return;

    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      const file = files[0];
      const ext = file.name.split('.').pop()?.toLowerCase();
      if (!ext || !this.allowedStoryExtensions.includes(ext)) {
        this.toastr.warning(`Only ${this.allowedStoryExtensions.join(', ')} files are supported.`, 'Invalid file type');
        return;
      }
      this.uploadFileToActivity(file);
      this.activeUploadMethodChange.emit('story');
    }
  }

  // Workspace upload methods
  onWorkspaceSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.resetState();

    if (input.files && input.files.length > 0) {
      const file = input.files[0];
      this.sessioneService.updateFileName(file.name);
      this.handleFileUpload(file);
      this.activeUploadMethodChange.emit('workspace');

      // After workspace is uploaded, navigate to the story component
      // This creates consistency with the story upload flow
      setTimeout(() => {
        this.router.navigate(['/projects/story', this.projectId], {
          queryParams: {
            sessionLoaded: 'true',
            source: 'file'
          }
        });
      }, 500); // Short delay to ensure data is processed
    }
  }

  private async handleFileUpload(file: File): Promise<void> {
    this.spinnerService.show();

    try {
      const savedState = await this.downloaderComponent.loadSavedState(file);

      if (!savedState) {
        throw new Error('Invalid file format');
      }

      const matchingTemplate = this.findMatchingTemplate(savedState.selectedTemplateColumns);
      let success = false;

      if (!matchingTemplate) {
        // If no matching template, continue but:
        // 1. Skip loading test data
        // 2. Don't update the template selection (leave the current one)
        savedState.selectedTemplate = ''; // Clear template to prevent update
        success = await this.downloaderComponent.restoreState(savedState, false, false);
        this.handleNoMatchingTemplate(savedState.selectedTemplateColumns);

        if (success) {
          this.toastr.warning(
            'Workspace restored partially without test data due to template mismatch. The current template has been preserved.',
            'Partial Restore'
          );
        }
      } else {
        // If template matches, restore everything including test data and update the template
        success = await this.downloaderComponent.restoreState(savedState, true, true);

        if (success) {
          this.templateSelected.emit(matchingTemplate.templateName);
          this.toastr.success('Workspace restored successfully', 'Restore Complete');
        }
      }

      if (!success) {
        this.toastr.error('Failed to restore workspace', 'Restore Error');
      }
    } catch (error) {
      console.error('Error handling file upload:', error);
      this.toastr.error('Failed to process the uploaded file.', 'Upload Error');
    } finally {
      this.spinnerService.hide();
    }
  }

  private findMatchingTemplate(uploadedColumns: string[]): any {
    // If uploaded columns are undefined or empty, return null immediately
    if (!uploadedColumns || !Array.isArray(uploadedColumns) || uploadedColumns.length === 0) {
      return null;
    }

    const normalizeColumns = (cols: any[]) => cols.map(col => {
      if (typeof col === 'string') { return col.toLowerCase(); }
      return (col.name || String(col)).toLowerCase();
    });

    return this.templates.find(template =>
      normalizeColumns(template.columns).length === normalizeColumns(uploadedColumns).length &&
      normalizeColumns(template.columns).every(col =>
        normalizeColumns(uploadedColumns).includes(col))
    );
  }

  private handleNoMatchingTemplate(uploadedColumns: any[]): void {
    this.mismatchColumns = Array.isArray(uploadedColumns) ? uploadedColumns.map(col => {
      if (typeof col === 'string') { return col; }
      if (col.name) { return col.name; }
      return JSON.stringify(col);
    }) : [];

    this.toastr.warning(
      'There is no matching suitable template for the uploaded file. Test data will not be loaded.',
      'Template Mismatch'
    );
    this.showMismatchContent = true;
    this.cdr.markForCheck();  // Marks the view to be checked during the next change detection run
  }

// Common clear file method (enhanced to handle both file types)
  clearFile(): void {
    Swal.fire({
      title: 'Are you sure?',
      text: 'This operation will cancel the current state. Make sure to download your workspace data to save your work.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, clear it', // Changed text slightly
      cancelButtonText: 'Cancel'
    }).then((result) => {
      if (result.isConfirmed) {
        this.resetState();
        this.sessioneService.resetAll();

        // Clear the story file input if it exists
        if (this.fileInputDocx?.nativeElement) {
          this.fileInputDocx.nativeElement.value = '';
        }
        this.fileName = ''; // Clear story file name state
        this.isUploadingStory = false; // Ensure upload flag is reset

        // Clear the workspace file input if it exists
        if (this.workspaceInput?.nativeElement) {
          this.workspaceInput.nativeElement.value = '';
        }
        // No need to clear workspace filename state explicitly as it uses fileName$

        this.activeUploadMethodChange.emit(null);
        this.cdr.markForCheck(); // Notify change detector

        // Use a more appropriate success message
        Swal.fire(
          'Cleared!',
          'The upload area has been reset.',
          'success'
        );
      }
    });
  }

  private resetState(): void {
    this.showMismatchContent = false;
    this.mismatchColumns = [];
    // No need to clear fileName here, clearFile handles it
    this.isUploadingStory = false; // Ensure flag is reset
  }
}
